import React from 'react'
import { makeStyles, createStyles } from '@mui/styles'
import {
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Paper
} from '@mui/material'
import {
  useTheme,
  Theme
} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { StepQuantity } from '../../_EOS/StepQuantity'
import { Batch } from '../../../pages/DispatchOtNew/entities/DispatchOt'
import { formatExpirationDate } from '../../../utils/dateHelper'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3)
      }
    },
    stepper: {
      padding: theme.spacing(2, 0)
    },
    stepContent: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    productInfo: {
      marginBottom: theme.spacing(3),
      textAlign: 'center'
    },
    imageContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '180px'
    },
    productImage: {
      maxWidth: '150px',
      height: 'auto',
      borderRadius: theme.shape.borderRadius
    },
    incidentFormContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%'
    },
    quantityControlContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: theme.spacing(4)
    },
    alertBox: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.info.light,
      borderRadius: theme.shape.borderRadius
    },
    stockItemCard: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5)
    },
    stockDetails: {
      flexGrow: 1
    },
    quantitySection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2)
    },
    actionButtons: {
      marginTop: theme.spacing(3),
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(1.5)
    }
  })
)

interface Stock {
  positionId: number
  positionName: string
  quantity: number
  currentQty: number
  isSelected: boolean
  batch?: Batch
}

interface InventoryIncidentType {
  typeInventoryIncidentId: number
  name: string
}

interface SkuInfo {
  skuId: number
  skuName: string
  sku: string
  url: string
  batch?: Batch
}

interface UnifiedDispatchModalProps {
  skuInfo: SkuInfo
  incidentTypes: InventoryIncidentType[]
  qtyIncident: number
  selectedIncidentTypeId: number
  selectedBatchId: number | undefined
  availableStocks: Stock[]
  isLoadingStocks: boolean
  isLoading: boolean
  onIncidentQtyChange: (qty: number) => void
  onIncidentTypeChange: (typeId: number) => void
  onBatchChange?: (batchId: string | null) => void
  onStockQtyChange: (stockIndex: number, qty: number) => void
  onNext: () => void
  onBack: () => void
  onCancel: () => void
  onSave: () => void
  activeStep: number
}

export const UnifiedDispatchModal: React.FC<UnifiedDispatchModalProps> = ({
  skuInfo,
  incidentTypes,
  qtyIncident,
  selectedIncidentTypeId,
  availableStocks,
  isLoadingStocks,
  isLoading,
  onIncidentQtyChange,
  onIncidentTypeChange,
  onStockQtyChange,
  onNext,
  onBack,
  onSave,
  activeStep
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const steps = ['Reportar Incidente', 'Completar desde Otra Posición']

  const canProceedToNext = () => {
    if (activeStep === 0) {
      return qtyIncident > 0 && selectedIncidentTypeId > 0
    }
    return true
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className={classes.stepContent}>
            <div className={classes.productInfo}>
              <Typography variant='overline' color='textSecondary'>
                SKU: {skuInfo.sku}
              </Typography>
              <Typography variant='h6' component='h2'>
                {skuInfo.skuName}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {skuInfo.batch?.batchNumber &&
                  `Lote: ${skuInfo.batch.batchNumber}`}
                {skuInfo.batch?.expirationDate &&
                  ` - Vence: ${formatExpirationDate(
                    skuInfo.batch.expirationDate,
                    'dd/MM/yyyy'
                  )}`}
              </Typography>
            </div>

            <Grid container spacing={isMobile ? 2 : 4} alignItems='stretch'>
              <Grid item xs={12} lg={6} className={classes.imageContainer}>
                <img
                  className={classes.productImage}
                  src={skuInfo.url}
                  alt='skuImage'
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <div className={classes.incidentFormContainer}>
                  <div className={classes.quantityControlContainer}>
                    <Typography variant='subtitle1' gutterBottom>
                      Cantidad con problema
                    </Typography>
                    <StepQuantity
                      onClickLess={() => onIncidentQtyChange(qtyIncident - 1)}
                      disabledLess={qtyIncident === 0}
                      onClickPlus={() => onIncidentQtyChange(qtyIncident + 1)}
                      disabledPlus={false}
                      quantity={qtyIncident}
                    />
                  </div>

                  <div>
                    <Typography variant='subtitle1' gutterBottom>
                      Tipo de problema
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      variant='outlined'
                      value={selectedIncidentTypeId || ''}
                      onChange={(e) =>
                        onIncidentTypeChange(parseInt(e.target.value))
                      }
                      disabled={qtyIncident === 0}
                    >
                      <MenuItem value='' disabled>
                        Seleccione un tipo
                      </MenuItem>
                      {incidentTypes.map((incident) => (
                        <MenuItem
                          key={incident.typeInventoryIncidentId}
                          value={incident.typeInventoryIncidentId}
                        >
                          {incident.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        )

      case 1:
        return (
          <div className={classes.stepContent}>
            <Typography variant='h6' gutterBottom>
              Completar desde Otra Posición
            </Typography>

            <Paper elevation={0} className={classes.alertBox}>
              <Typography>
                Necesitas completar <b>{qtyIncident} unidades</b> desde las
                posiciones disponibles.
              </Typography>
            </Paper>

            {isLoadingStocks ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                py={5}
              >
                <CircularProgress />
                <Typography style={{ marginLeft: '16px' }}>
                  Buscando stock...
                </Typography>
              </Box>
            ) : availableStocks.length === 0 ? (
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                padding='40px'
                textAlign='center'
              >
                <Typography variant='h6' color='textSecondary' gutterBottom>
                  No hay unidades disponibles
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  No se encontraron posiciones con stock disponible para este SKU.
                  Puedes continuar para completar la operación sin mover unidades.
                </Typography>
              </Box>
            ) : (
              <div>
                {availableStocks.map((stock, index) => (
                  <Paper
                    key={stock.positionId}
                    className={classes.stockItemCard}
                    elevation={1}
                  >
                    <Grid container alignItems='center'>
                      <Grid item className={classes.stockDetails}>
                        <Typography variant='subtitle1'>
                          {stock.positionName}
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          {stock.batch?.batchNumber &&
                            `Lote: ${stock.batch.batchNumber} - `}
                          Disponible: {stock.quantity - stock.currentQty}{' '}
                          unidades
                        </Typography>
                      </Grid>
                      <Grid item>
                        <div className={classes.quantitySection}>
                          <Typography variant='body2'>A usar:</Typography>
                          <StepQuantity
                            onClickLess={() =>
                              onStockQtyChange(index, stock.currentQty - 1)
                            }
                            disabledLess={stock.currentQty === 0}
                            onClickPlus={() =>
                              onStockQtyChange(index, stock.currentQty + 1)
                            }
                            disabledPlus={
                              stock.currentQty >= stock.quantity ||
                              availableStocks.reduce(
                                (sum, s) => sum + s.currentQty,
                                0
                              ) >= qtyIncident
                            }
                            quantity={stock.currentQty}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </div>
            )}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className={classes.container}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel={!isMobile}
        className={classes.stepper}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider />

      {renderStepContent(activeStep)}

      <div className={classes.actionButtons}>
        {activeStep > 0 && (
          <Button variant='outlined' onClick={onBack} disabled={isLoading}>
            Anterior
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            variant='contained'
            color='primary'
            onClick={onNext}
            disabled={!canProceedToNext() || isLoading}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant='contained'
            color={availableStocks.length === 0 ? 'secondary' : 'primary'}
            onClick={onSave}
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size='1rem' color='inherit' />
              ) : null
            }
            style={availableStocks.length === 0 ? { backgroundColor: '#ff9800', color: 'white' } : undefined}
          >
            {availableStocks.length === 0 ? 'Finalizar sin completar' : 'Completar Operación'}
          </Button>
        )}
      </div>
    </div>
  )
}
